import ReactECharts from "echarts-for-react";
import type { ChartBlock } from "../../api";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4", "#EC4899", "#64748B"];

const cssVar = (name: string, fallback: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

const compact = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });
const full = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const formatValue = (value: unknown) => (typeof value === "number" ? full.format(value) : String(value ?? "—"));
const formatAxis = (value: number) => (Math.abs(value) >= 1000 ? compact.format(value) : full.format(value));

// Rankings with long category names (DMAs, campaigns) read better as horizontal bars.
const isHorizontal = (block: ChartBlock) => {
  if (block.chart_type !== "bar" || block.series.length !== 1 || block.x.length > 15) return false;
  const avgLabel = block.x.reduce<number>((sum, x) => sum + String(x).length, 0) / block.x.length;
  return avgLabel > 10;
};

export const chartHeight = (block: ChartBlock) => (isHorizontal(block) ? Math.max(260, block.x.length * 34 + 90) : 360);

function buildOption(block: ChartBlock) {
  const text = cssVar("--text-secondary", "#6B7280");
  const grid = cssVar("--border-subtle", "#E5E7EB");
  const surface = cssVar("--bg-base", "#FFFFFF");
  const kind = block.chart_type;
  const multi = block.series.length > 1;
  const common = {
    color: COLORS,
    textStyle: { fontFamily: "Geist, Inter, system-ui, sans-serif", color: text },
    title: { text: block.title, left: 0, textStyle: { fontSize: 14, fontWeight: 600, color: cssVar("--text-primary", "#111827") } },
    tooltip: {
      trigger: kind === "pie" || kind === "scatter" ? "item" : "axis",
      confine: true,
      valueFormatter: formatValue,
      axisPointer: { type: kind === "bar" || kind === "stacked_bar" ? "shadow" : "line" },
    },
    legend: { show: kind === "pie" || multi, bottom: 0, icon: "roundRect", itemWidth: 12, itemHeight: 8, textStyle: { color: text } },
    animation: false, // keeps PNG/PDF export from catching a half-drawn chart
  };

  if (kind === "pie") {
    return {
      ...common,
      series: [
        {
          type: "pie",
          radius: ["48%", "72%"],
          center: ["50%", "50%"],
          itemStyle: { borderRadius: 6, borderColor: surface, borderWidth: 2 },
          label: { formatter: "{b}\n{d}%", color: text, lineHeight: 16 },
          labelLine: { length: 10, length2: 8 },
          data: block.x.map((name, i) => ({ name: String(name), value: block.series[0]?.values[i] ?? 0 })),
        },
      ],
    };
  }

  const horizontal = isHorizontal(block);
  const categoryAxis = {
    type: "category",
    data: block.x.map(String),
    name: horizontal ? "" : block.x_label ?? "",
    nameLocation: "middle",
    nameGap: 28,
    inverse: horizontal, // first (largest) item on top
    axisLabel: { color: text, hideOverlap: true, width: horizontal ? 140 : undefined, overflow: "truncate" },
    axisLine: { lineStyle: { color: grid } },
    axisTick: { show: false },
  };
  const valueAxis = {
    type: "value",
    name: block.y_label ?? "",
    // Horizontal bars: the value axis runs along the bottom, so center its name under it.
    nameLocation: horizontal ? "middle" : "end",
    nameGap: horizontal ? 30 : 15,
    nameTextStyle: { color: text },
    scale: kind === "line", // trends read better without forcing zero (bars and areas keep it)
    axisLabel: { color: text, formatter: formatAxis },
    splitLine: { lineStyle: { color: grid, type: "dashed" } },
  };
  const isBar = kind === "bar" || kind === "stacked_bar";
  const showValues = kind === "bar" && !multi && block.x.length <= 12;

  return {
    ...common,
    grid: { left: 8, right: horizontal ? 48 : 16, top: 48, bottom: multi ? 40 : horizontal && block.y_label ? 36 : 16, containLabel: true },
    xAxis: horizontal ? valueAxis : categoryAxis,
    yAxis: horizontal ? categoryAxis : valueAxis,
    series: block.series.map((s, i) => {
      const color = COLORS[i % COLORS.length];
      return {
        name: s.name,
        data: s.values,
        type: isBar ? "bar" : kind === "scatter" ? "scatter" : "line",
        stack: kind === "stacked_bar" ? "total" : undefined,
        barMaxWidth: 44,
        itemStyle: isBar && kind !== "stacked_bar" ? { borderRadius: horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0] } : undefined,
        label: showValues
          ? { show: true, position: horizontal ? "right" : "top", color: text, fontSize: 11, formatter: (p: { value: number }) => formatAxis(p.value) }
          : undefined,
        smooth: kind === "line" || kind === "area" ? 0.3 : undefined,
        showSymbol: kind === "scatter" || block.x.length <= 24,
        symbol: "circle",
        symbolSize: kind === "scatter" ? 10 : 6,
        lineStyle: kind === "line" || kind === "area" ? { width: 2.5 } : undefined,
        areaStyle:
          kind === "area"
            ? {
                color: {
                  type: "linear", x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [{ offset: 0, color: `${color}55` }, { offset: 1, color: `${color}05` }],
                },
              }
            : undefined,
      };
    }),
  };
}

export default function CanvasChart({ block }: { block: ChartBlock }) {
  return <ReactECharts option={buildOption(block)} notMerge style={{ height: chartHeight(block), width: "100%" }} />;
}
